import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

export const SECTION_LABELS = [["purpose", "Purpose"], ["scope", "Scope"], ["legal_basis", "Legal and regulatory basis"], ["definitions", "Definitions"],
  ["policy_statement", "Policy statement"], ["procedure", "Procedure"], ["roles", "Roles and responsibilities"], ["records", "Records and documentation"],
  ["training", "Training and competence"], ["monitoring", "Monitoring, audit and review"], ["inspector_focus", "What inspectors will look for"],
  ["related_documents", "Related documents and forms"], ["appendices", "Appendices"]];

const PARAGRAPHS = ["purpose", "scope", "policy_statement"];
const lines = (arr) => (arr || []).join("\n");
const unlines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const paras = (arr) => (Array.isArray(arr) ? arr : [arr || ""]).join("\n\n");
const unparas = (s) => s.split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);

const Group = ({ items, onChange, headKey, listKey, headLabel, idBase }) => (
  <div className="space-y-3">
    {(items || []).map((it, i) => (
      <div key={i} className="rounded-lg border border-border p-3 space-y-2 bg-background">
        <div className="flex gap-2">
          <Input value={it[headKey] || ""} placeholder={headLabel} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [headKey]: e.target.value } : x)))} data-testid={`${idBase}-${i}-head`} />
          <Button type="button" variant="ghost" size="icon" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${idBase}-${i}-remove`}><Trash2 className="w-4 h-4" /></Button>
        </div>
        <Textarea rows={Math.min(12, Math.max(3, (it[listKey] || []).length + 1))} value={lines(it[listKey])} placeholder="One item per line"
          onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [listKey]: unlines(e.target.value) } : x)))} data-testid={`${idBase}-${i}-items`} />
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" onClick={() => onChange([...(items || []), { [headKey]: "", [listKey]: [] }])} data-testid={`${idBase}-add`}><Plus className="w-4 h-4 mr-1" /> Add</Button>
  </div>
);

export function PolicySectionEditor({ content, onChange, idPrefix = "policy" }) {
  const set = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-6">
      <label className="block text-sm">
        <span className="font-medium">Review frequency</span>
        <Input className="mt-1 max-w-xs" value={content.review_frequency || ""} onChange={(e) => set("review_frequency", e.target.value)} data-testid={`${idPrefix}-review-frequency`} />
      </label>
      {SECTION_LABELS.map(([k, label]) => (
        <section key={k} data-testid={`${idPrefix}-section-${k}`}>
          <h3 className="font-display font-semibold text-base mb-2">{label}</h3>
          {PARAGRAPHS.includes(k) && (
            <Textarea rows={6} value={paras(content[k])} placeholder="Separate paragraphs with a blank line" onChange={(e) => set(k, unparas(e.target.value))} data-testid={`${idPrefix}-${k}`} />
          )}
          {k === "definitions" && (
            <Textarea rows={6} placeholder="Term: meaning — one per line" value={(content[k] || []).map((d) => `${d.term}: ${d.meaning}`).join("\n")}
              onChange={(e) => set(k, unlines(e.target.value).map((l) => { const [term, ...rest] = l.split(":"); return { term: term.trim(), meaning: rest.join(":").trim() }; }))} data-testid={`${idPrefix}-${k}`} />
          )}
          {k === "procedure" && <Group items={content[k]} onChange={(v) => set(k, v)} headKey="heading" listKey="steps" headLabel="Sub-section heading" idBase={`${idPrefix}-procedure`} />}
          {k === "roles" && <Group items={content[k]} onChange={(v) => set(k, v)} headKey="role" listKey="responsibilities" headLabel="Role" idBase={`${idPrefix}-roles`} />}
          {k === "appendices" && <Group items={content[k]} onChange={(v) => set(k, v)} headKey="title" listKey="items" headLabel="Appendix title" idBase={`${idPrefix}-appendices`} />}
          {!PARAGRAPHS.includes(k) && !["definitions", "procedure", "roles", "appendices"].includes(k) && (
            <Textarea rows={Math.min(14, Math.max(4, (content[k] || []).length + 1))} value={lines(content[k])} placeholder="One item per line" onChange={(e) => set(k, unlines(e.target.value))} data-testid={`${idPrefix}-${k}`} />
          )}
        </section>
      ))}
    </div>
  );
}
